<template>
  <div class="client-questions-card floating-widget">
    <div class="card-header">
      <h2 class="card-title">Client Questions</h2>
      <div class="controls">
        <div class="control-group">
          <label for="sort-by-questions">Sort By:</label>
          <select id="sort-by-questions" v-model="sortBy" class="form-select" @change="fetchQuestions">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <div class="control-group">
          <label for="rows-per-page-questions">Rows per page:</label>
          <select id="rows-per-page-questions" v-model="pagination.pageSize" class="form-select"
            @change="fetchQuestions">
            <option>5</option>
            <option>10</option>
            <option>20</option>
          </select>
        </div>

        <div class="pagination">
          <button @click="prevPage" :disabled="pagination.currentPage === 1" class="pagination-arrow">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="pagination-label">
            <strong>{{ paginationRange }}</strong> of {{ paginationTotal }}
          </span>
          <button @click="nextPage" :disabled="pagination.currentPage === totalPages" class="pagination-arrow">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="questions-list">
      <div v-if="isLoading" class="loading-state">Loading questions...</div>
      <div v-else-if="questions.length === 0" class="empty-state">No pending questions to display.</div>
      <div 
        v-else 
        v-for="question in questions" 
        :key="question.id" 
        class="question-row clickable-row"
        @click="viewQuestion(question)"
      >
        <div class="question-info-wrapper">
          <div class="question-date">
            <div class="date-formatted">{{ formatDate(question.created_at) }}</div>
          </div>
          
          <div class="question-info">
            <div class="question-lead">
              <span class="org-name">{{ question.proposal.Lead.auth_organisation_name || question.proposal.Lead.organisation_name }}</span>
              <span class="status-badge" :style="{ backgroundColor: getStatusColor(question.status) }">
                {{ getQuestionStatusText(question.status) }}
              </span>
            </div>
            
            <div class="question-meta">
              <template v-if="['Main Lead', 'Primary Contact', ''].includes(question.question_by_email_role)">
                <span class="sub-label-gray">
                  {{ question.question_by_email || question.proposal.Lead.email }} 
                  <strong>({{ question.question_by_email_role || 'Main Lead' }})</strong>
                </span>
              </template>
              <template v-else>
                <span class="sub-label-gray">
                  {{ question.question_by_email }} 
                  <strong>(Shared Access User - {{ question.question_by_email_role || 'User' }})</strong>
                </span>
              </template>
              <i class="fas fa-copy copy-icon" @click.stop="copyEmail(question.question_by_email || question.proposal.Lead.email)" title="Copy Email"></i>
            </div>

            <div class="question-proposal-title">Proposal: {{ question.proposal.proposal_title }}</div>
            <div class="question-text" :title="question.question">
              {{ truncateText(question.question, 140) }}
            </div>
          </div>
        </div>

        <div class="question-action">
          <button type="button" class="btn-svg-icon position-relative" title="View Question" @click.stop="viewQuestion(question)">
              <img src="@/assets/img/icons/preview.svg" alt="View Question" class="icon-svg" />
              <span v-if="!question.is_opened" class="exclamation-icon">
                <i class="fa-solid fa-circle-exclamation"></i>
              </span>
          </button>
          <button type="button" class="btn-svg-icon" title="Mark as Resolved" :disabled="!canManageQuestions(question)" @click.stop="resolveQuestion(question.id)">
            <img src="@/assets/img/icons/Save.svg" alt="Resolve Question" class="icon-svg" />
          </button>
           <button type="button" :disabled="!canManageQuestions(question)" class="btn-svg-icon" title="Send Reply" @click.stop="sendReply(question)">
            <img src="@/assets/img/icons/send.svg" alt="Send Reply" class="icon-svg" />
          </button>
        </div>
      </div>
    </div>
  </div>

  <div v-if="isModalOpen" class="modal-backdrop-custom" @click="closeModalOnOutsideClick">
      <div class="modal-content-custom" @click.stop>
          <button @click="isModalOpen = false" class="close-button-custom">&times;</button>
          <h5 class="modal-title-custom">Question Details</h5>
          <hr>
          <div class="modal-body" v-if="currentQuestion">
              <p>
                <strong>Organisation:</strong> 
                {{ currentQuestion.proposal.Lead.auth_organisation_name || currentQuestion.proposal.Lead.organisation_name }}
              </p>
              <p>
                <strong>Questioned By:</strong> 
                {{ currentQuestion.question_by_email || currentQuestion.proposal.Lead.email }} 
                (<strong>{{ currentQuestion.question_by_email_role || 'Main Lead' }}</strong>)
                <i class="fas fa-copy copy-icon" @click.stop="copyEmail(currentQuestion.question_by_email || currentQuestion.proposal.Lead.email)"></i>
              </p>
              <p>
                <strong>Account Type:</strong> 
                {{ ['Main Lead', 'Primary Contact', ''].includes(currentQuestion.question_by_email_role) ? 'Primary Contact' : 'Shared Access User' }}
              </p>
              <p><strong>Proposal:</strong> {{ currentQuestion.proposal.proposal_title }}</p>
              <hr>
              <p><strong>Question:</strong></p>
              <div class="full-question-box">{{ currentQuestion.question }}</div>
          </div>
      </div>
  </div>

  <ConfirmModal
     v-model="showConfirmModal"
     :title="confirmModalTitle"
     :message="confirmModalMessage"
     @confirm="onConfirmAction"
  />
</template>

<script>
import { truncateLongText } from '@/plugins/helper.js';

export default {
  name: 'ClientQuestionsWidget',
  data() {
    return {
      isLoading: false,
      questions: [],
      sortBy: 'newest',
      pagination: {
        currentPage: 1,
        pageSize: 5,
        totalItems: 0,
      },
      isModalOpen: false,
      currentQuestion: null,
      colorForStatus: { pending: '#F3994D', resolved: '#00A54F' },
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      pendingActionId: null,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
    },
    paginationRange() {
      const total = this.pagination.totalItems;
      if (total === 0) return '0';
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
      const end = Math.min(start + this.pagination.pageSize - 1, total);
      return `${start}-${end}`;
    },
    paginationTotal() {
      return this.pagination.totalItems;
    },
  },
  watch: {
    'pagination.pageSize'() {
      this.pagination.currentPage = 1;
      this.fetchQuestions();
    },
    'pagination.currentPage'() {
      this.fetchQuestions();
    },
    sortBy() {
      this.fetchQuestions();
    }
  },
  created() {
    this.fetchQuestions();
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },
    truncateText(text, length) {
      return truncateLongText(text, length);
    },
    getQuestionStatusText(status) { 
      return status === 0 ? 'Pending' : 'Resolved'; 
    },
    getStatusColor(status) { 
      return status === 0 ? this.colorForStatus.pending : this.colorForStatus.resolved; 
    },
    async fetchQuestions() {
      this.isLoading = true;
      try {
        const sortOrder = this.sortBy === 'newest' ? 'desc' : 'asc';
        const response = await this.$axios.get('/questions/get-all', {
          params: {
            page: this.pagination.currentPage,
            pageSize: this.pagination.pageSize,
            sortBy: 'created_at',
            sortOrder: sortOrder,
            status: 0, 
          }
        });
        if (response.data.success) {
          this.questions = response.data.questions || [];
          this.pagination.totalItems = response.data.totalItems;
        }
      } catch (error) {
        console.error("Failed to fetch questions:", error);
      } finally {
        this.isLoading = false;
      }
    },
    viewQuestion(question) {
      this.currentQuestion = question;
      this.isModalOpen = true;

      if (!question.is_opened) {
          this.markQuestionAsOpened(question.id);
      }
    },
    async markQuestionAsOpened(questionId) {
        try {
            const response = await this.$axios.put(`/questions/${questionId}/mark-opened`);
            if (response.data.success) {
                const index = this.questions.findIndex(q => q.id === questionId);
                if (index !== -1) {
                    this.questions[index].is_opened = true;
                }
            }
        } catch (error) {
            console.error("Error marking question as opened:", error);
        }
    },
    async resolveQuestion(questionId) {
      this.pendingActionId = questionId;
      this.confirmModalTitle = 'Resolve Question';
      this.confirmModalMessage = 'Are you sure you want to mark this question as resolved?';
      this.showConfirmModal = true;
    },
    async onConfirmAction() {
      const questionId = this.pendingActionId;
      this.pendingActionId = null;
      if (!questionId) return;
      try {
        const response = await this.$axios.put(`/questions/${questionId}/resolve`);
        if (response.data.success) {
          this.$toast.success("Question resolved successfully.");
          this.fetchQuestions();
        }
      } catch (error) {
        this.$toast.error("An error occurred while resolving the question.");
      }
    },
    closeModalOnOutsideClick(event) {
        if (event.target.classList.contains('modal-backdrop-custom')) {
            this.isModalOpen = false;
        }
    },
    async copyEmail(email) {
      try {
          await navigator.clipboard.writeText(email);
          this.$toast.success("Email copied to clipboard!");
      } catch (err) {
          this.$toast.error("Failed to copy email.");
      }
    },
    nextPage() {
      if (this.pagination.currentPage < this.totalPages) this.pagination.currentPage++;
    },
    prevPage() {
      if (this.pagination.currentPage > 1) this.pagination.currentPage--;
    },
    canManageQuestions(question) {
      if (this.$store.state.auth.isAdmin) {
        return false;
      }
      if (this.$store.getters['auth/isGlobalSuperAdmin']) {
        return true;
      }
      if (this.$store.state.auth.isConsultant) {
        const currentUserId = this.$store.state.auth.currentUser?.id || 0;
        const creatorId = question.proposal?.Lead?.created_by || question.proposal?.created_by || 0;
        return creatorId > 0 && currentUserId === creatorId;
      }
      return false;
    },
    sendReply(question) {
      if (!this.canManageQuestions(question)) return;
      const email = question.question_by_email || question.proposal?.Lead?.email;
      if (email) {
        window.location.href = `mailto:${email}?subject=RE: ${encodeURIComponent(question.proposal?.proposal_title || '')}`;
      }
    }
  }
};
</script>

<style scoped>
.client-questions-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 0;
  font-family: 'Roboto', sans-serif;
  max-width: 100%;
}

.floating-widget {
  position: relative;
  width: 100%;
  z-index: 10;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group label {
    color: #6c757d;
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
}

.form-select {
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  padding: 8px 40px 8px 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #fff;
}

/* SCROLLABLE LIST CONTAINER */
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px; /* Forces scrolling when content exceeds this height */
  overflow-y: auto;
  padding: 16px 24px;
  /* Smooth scrolling for touch devices */
  -webkit-overflow-scrolling: touch; 
}

/* CUSTOM SCROLLBAR DESIGN */
.questions-list::-webkit-scrollbar {
  width: 6px;
}
.questions-list::-webkit-scrollbar-track {
  background: #f1f1f1; 
  border-radius: 10px;
  margin: 10px 0; /* Keeps track slightly padded from edges */
}
.questions-list::-webkit-scrollbar-thumb {
  background: #c1c1c1; 
  border-radius: 10px;
}
.questions-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8; 
}

.question-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s ease;
  flex-shrink: 0; /* Prevents rows from squishing inside the flex container */
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  border-color: #00A0C1;
  box-shadow: 0 4px 10px rgba(0, 160, 193, 0.1);
  background-color: #fcfdfe;
}

.question-info-wrapper {
  display: flex;
  align-items: stretch;
  flex-grow: 1;
}

.question-date {
  background-color: #435666;
  color: #fff;
  min-width: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  text-align: center;
}

.date-formatted {
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.question-info {
  flex-grow: 1;
  padding: 15px 25px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.org-name {
  font-weight: 700;
  font-size: 1.1rem;
  color: #333;
  margin-right: 10px;
}

.status-badge {
  font-size: 0.7rem;
  padding: 2px 10px;
  border-radius: 5px;
  color: white;
  text-transform: uppercase;
  font-weight: bold;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.sub-label-gray {
  font-size: 0.85rem;
  color: #6c757d;
}

.question-proposal-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #00A0C1;
}

.question-text {
  color: #555;
  font-size: 0.95rem;
  line-height: 1.4;
  margin-top: 6px;
}

.copy-icon {
  cursor: pointer;
  color: #adb5bd;
  font-size: 0.85rem;
  padding: 5px;
  transition: 0.2s;
}

.copy-icon:hover {
  color: #00A0C1;
  background-color: #e9ecef;
  border-radius: 4px;
}

.question-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
}

.position-relative {
    position: relative;
}

.exclamation-icon {
    position: absolute;
    top: -5px;
    right: -5px;
    color: #F3994D; 
    font-size: 1.1em;
    background-color: white;
    border-radius: 50%;
    line-height: 1;
}

.btn-svg-icon {
   width: 30px;
   height: 30px;
   border-radius: 50%;
   display: flex;
   align-items: center;
   justify-content: center;
   background-color: transparent;
   border: none;
   cursor: pointer;
   transition: background-color 0.2s ease;
}

.btn-svg-icon:hover:not(:disabled) {
   background-color: #00A0C1;
}

.btn-svg-icon:hover:not(:disabled) .icon-svg {
   filter: brightness(0) invert(1);
}

.btn-svg-icon:disabled {
   opacity: 0.3;
   cursor: not-allowed;
   pointer-events: none;
}

.icon-svg {
   width: 30px;
   height: 30px;
}

/* PAGINATION */
.pagination {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #fff;
}

.pagination-label {
  font-size: 0.9rem;
  padding: 0 8px;
}

.pagination-arrow {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #00A0C1;
  padding: 8px 12px;
}

.pagination-arrow:disabled {
  color: #ced4da;
}

/* MODAL STYLES */
.modal-backdrop-custom {
    position: fixed;
    top: 0; left: 0; width: 100vw; height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex; justify-content: center; align-items: center;
    z-index: 1050;
}

.modal-content-custom {
    position: relative;
    background-color: white;
    padding: 2.5rem 2rem;
    border-radius: 16px;
    width: 100%;
    max-width: 550px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-title-custom {
    font-weight: bold;
    font-size: 1.25rem;
}

.close-button-custom {
    position: absolute;
    top: 1rem; right: 1rem;
    font-size: 2rem;
    color: #888;
    background: none; border: none;
    cursor: pointer;
}

.full-question-box {
    background-color: #f8f9fa;
    padding: 18px;
    border-radius: 12px;
    border: 1px solid #dee2e6;
    white-space: pre-wrap;
    font-size: 1rem;
    color: #435666;
    line-height: 1.6;
}
</style>